// shared/inventory-instance-root.ts — paint a saved inventory instance from its possessor's browser compile.
//
// PHP serves an inventory instance (item.php, object_inc.php, ft_inventory_tile_mount()) as a SELECT shell
// carrying `_inventory_refusal: 'unported:inventory-instance-root'`: the instance's own tree facet holds only
// its CONTENTS, and the row's own statuses / charges / media arrive in its POSSESSOR's snapshot. The
// possessor's compiled tree already holds this row as a finished node (definition, enchantment, economy,
// statuses, pictures, nested contents under inventory_in_container), so a surface reads the possessor
// through the page's one inventory runtime (window.ftAdoptInventoryBase, websocket/base-patch.ts) and paints
// that node. A possessor the viewer may not read, a compile refusal or a missing node paints a named refusal.
//
// Page state (per window): the runtime's latest result per possessor, one in-flight read per possessor, and
// the WATCHES — one per claimed container, with a unique token. Every compile or refusal of a possessor —
// ours, an earlier one, or a later base_patch — reaches each watch that still owns its container; a reused
// container releases its old watch, and an attached-then-removed container releases at the next event or
// the next claim's sweep. Surfaces (object card, inventory tile) supply only the painter.

import { inventoryField } from './array-types';
import { inventoryRowList } from '../janus-engine/compile/inventory-raw-readers';

type Row = Record<string, unknown>;
type BaseEventDetail = { entityId?: unknown; computed?: unknown; stages?: unknown };

export const INVENTORY_INSTANCE_ROOT = 'unported:inventory-instance-root';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const COMPILE_TIMEOUT_MS = 60000;
const record = (value: unknown): value is Row => value !== null && typeof value === 'object' && !Array.isArray(value);
const upperId = (value: unknown): string => String(value ?? '').toUpperCase();

export interface InventoryInstanceRootRequest { inventoryId: string; possessorId: string }

/** The shell PHP serves for a saved instance, with the ids its possessor compile needs; else null. */
export function inventoryInstanceRootRequest(value: unknown): InventoryInstanceRootRequest | null {
    if (!record(value) || value._inventory_refusal !== INVENTORY_INSTANCE_ROOT) return null;
    const inventoryId = String(inventoryField(value, 'inventory_id') ?? '');
    const possessorId = String(inventoryField(value, 'possess_id') ?? '');
    if (!UUID.test(inventoryId) || !UUID.test(possessorId)) return null;
    return { inventoryId: inventoryId.toUpperCase(), possessorId: possessorId.toUpperCase() };
}

/** Depth-first over a compiled tree; a container's contents are compiled nodes under inventory_in_container. */
export function findCompiledInventoryNode(nodes: unknown, inventoryId: string): Row | null {
    for (const node of inventoryRowList(nodes)) {
        if (upperId(inventoryField(node, 'inventory_id')) === inventoryId) return node;
        const nested = findCompiledInventoryNode(node.inventory_in_container, inventoryId);
        if (nested) return nested;
    }
    return null;
}

/** A surface's painter. `node` returns false when the surface is finished (e.g. a tile replaced its mount). */
export interface InventoryInstancePainter {
    pending?(container: HTMLElement): void;
    node(node: Row, container: HTMLElement): boolean;
    refusal(reason: string, container: HTMLElement): void;
}

interface Watch {
    token: symbol;
    container: HTMLElement;
    request: InventoryInstanceRootRequest;
    painter: InventoryInstancePainter;
    /** The last terminal result shown: a compiled node or a named refusal. */
    status: 'pending' | 'card' | 'refused';
    /** Observed in the document at least once — only then does detachment mean removal. */
    attached: boolean;
    timer: ReturnType<typeof setTimeout>;
}
type Latest = { computed: unknown } | { refused: string };
interface PageState {
    latest: Map<string, Latest>;
    reads: Map<string, Promise<void>>;
    watches: Map<string, Set<Watch>>;
    byContainer: WeakMap<HTMLElement, Watch>;
}
const pages = new WeakMap<object, PageState>();

function page(): PageState {
    let state = pages.get(window);
    if (state) return state;
    const created: PageState = { latest: new Map(), reads: new Map(), watches: new Map(), byContainer: new WeakMap() };
    pages.set(window, created);
    window.addEventListener('ft:inventory-base-compiled', event => {
        const detail = (event as CustomEvent<BaseEventDetail>).detail, id = upperId(detail?.entityId);
        created.latest.set(id, { computed: detail.computed });
        for (const watch of [...created.watches.get(id) ?? []]) deliverIsolated(created, watch, { computed: detail.computed });
    });
    window.addEventListener('ft:inventory-base-refused', event => {
        const detail = (event as CustomEvent<BaseEventDetail>).detail, id = upperId(detail?.entityId);
        const stages = Array.isArray(detail?.stages) ? detail.stages.map(String) : [];
        const result = { refused: `unported:inventory-base:${stages.join(',')}` };
        created.latest.set(id, result);
        for (const watch of [...created.watches.get(id) ?? []]) deliverIsolated(created, watch, result);
    });
    return created;
}
// The runtime only emits after the pre-template bundle loads, below every surface bundle; listen from eval.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') page();

/** The watch while it still owns its container; otherwise released. Pre-attach renders stay live until attached. */
function owns(state: PageState, watch: Watch): boolean {
    if (state.byContainer.get(watch.container) !== watch) { release(state, watch); return false; }
    if (watch.container.isConnected) watch.attached = true;
    else if (watch.attached) { release(state, watch); return false; }
    return true;
}

function release(state: PageState, watch: Watch): void {
    clearTimeout(watch.timer);
    const set = state.watches.get(watch.request.possessorId);
    set?.delete(watch);
    if (set && set.size === 0) state.watches.delete(watch.request.possessorId);
    if (state.byContainer.get(watch.container) === watch) {
        state.byContainer.delete(watch.container);
        delete watch.container.dataset.ftInventoryInstance;
    }
}

function settle(watch: Watch, status: 'card' | 'refused'): void {
    watch.status = status;
    // Side panels attach the container right after rendering into it.
    setTimeout(() => { if (watch.container.isConnected) watch.attached = true; }, 0);
}

function refuse(state: PageState, watch: Watch, reason: string): void {
    if (!owns(state, watch)) return;
    watch.painter.refusal(reason, watch.container);
    settle(watch, 'refused');
}

function deliver(state: PageState, watch: Watch, result: Latest): void {
    if ('refused' in result) { refuse(state, watch, result.refused); return; }
    if (!owns(state, watch)) return;
    const node = findCompiledInventoryNode(result.computed, watch.request.inventoryId);
    if (!node) { refuse(state, watch, `${INVENTORY_INSTANCE_ROOT}:not-in-possessor`); return; }
    const keep = watch.painter.node(node, watch.container);
    settle(watch, 'card');
    if (!keep) release(state, watch);
}

/** One surface's painter failure never keeps the event from the other watches of that possessor. */
function deliverIsolated(state: PageState, watch: Watch, result: Latest): void {
    try { deliver(state, watch, result); }
    catch (error) { console.warn('[inventory] instance root painter', { ...watch.request, error }); }
}

/** The page runtime's bridge. Surfaces render ABOVE the pre-template bundle that installs it.
 *  'interactive' may still precede DOMContentLoaded, so that state waits for the window load. */
function inventoryRuntime(): Promise<Window['ftAdoptInventoryBase']> {
    if (typeof window.ftAdoptInventoryBase === 'function' || document.readyState === 'complete')
        return Promise.resolve(window.ftAdoptInventoryBase);
    return new Promise(resolve => {
        const done = (): void => resolve(window.ftAdoptInventoryBase);
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', done, { once: true });
        else window.addEventListener('load', done, { once: true });
    });
}

/** One read per possessor at a time; the result arrives as a runtime event, failures as a named rejection. */
function readPossessor(state: PageState, possessorId: string): Promise<void> {
    const existing = state.reads.get(possessorId);
    if (existing) return existing;
    const read = (async () => {
        const adopt = await inventoryRuntime();
        if (typeof adopt !== 'function') throw new Error(`${INVENTORY_INSTANCE_ROOT}:no-inventory-runtime`);
        const response = await fetch(`${BASE_URL}options/api/base_entity_inc.php?entity_id=${encodeURIComponent(possessorId)}&entity=inventory&cold=0`);
        if (!response.ok) throw new Error(`${INVENTORY_INSTANCE_ROOT}:possessor-${response.status}`);
        await adopt({ _inventory_base: await response.json() as unknown });
    })();
    state.reads.set(possessorId, read);
    const clear = (): void => { if (state.reads.get(possessorId) === read) state.reads.delete(possessorId); };
    read.then(clear, clear);
    return read;
}

/** Every new render of a container releases its previous instance watch (side-panel reuse, card replacement). */
export function releaseInventoryInstanceClaim(container: HTMLElement): void {
    const state = page(), watch = state.byContainer.get(container);
    if (watch) release(state, watch);
    else delete container.dataset.ftInventoryInstance;
}

/** Claim `container` for the instance and paint it from its possessor's compiles via `painter`. */
export function claimInventoryInstance(request: InventoryInstanceRootRequest, container: HTMLElement,
    painter: InventoryInstancePainter): void {
    const state = page();
    releaseInventoryInstanceClaim(container);
    for (const set of [...state.watches.values()]) for (const live of [...set]) {
        if (live.container.isConnected) live.attached = true;
        else if (live.attached) release(state, live);
    }
    const watch: Watch = { token: Symbol(request.inventoryId), container, request, painter, status: 'pending',
        attached: container.isConnected, timer: setTimeout(() => {
            if (watch.status === 'pending') refuse(state, watch, `${INVENTORY_INSTANCE_ROOT}:possessor-timeout`);
        }, COMPILE_TIMEOUT_MS) };
    state.byContainer.set(container, watch);
    const set = state.watches.get(request.possessorId) ?? new Set<Watch>();
    set.add(watch); state.watches.set(request.possessorId, set);
    container.dataset.ftInventoryInstance = request.inventoryId;
    painter.pending?.(container);
    const latest = state.latest.get(request.possessorId);
    if (latest) deliverIsolated(state, watch, latest);
    // A known compile that holds the row is the runtime's current truth (later compiles still arrive); a refusal or a
    // compile without the row (it may predate the row's arrival) reads the possessor again.
    if (latest && 'computed' in latest && watch.status === 'card') return;
    readPossessor(state, request.possessorId).catch((error: unknown) => {
        console.warn('[inventory] instance root', { ...request, error });
        // The runtime may already have painted this possessor for this viewer (another read on the page); a
        // failure of this read does not unmake that truth, and the next possessor event repaints.
        if (watch.status === 'card') return;
        refuse(state, watch, error instanceof Error && error.message.startsWith('unported:') ? error.message : `${INVENTORY_INSTANCE_ROOT}:possessor-read`);
    });
}
